import type { Location } from "vue-router";
import type { ContestEntryApplicantFormValue } from "@/components/organisms/event/contestEntryApplicantInfoFormEventHandlers";
import type { ContestEntryWorkFormValue } from "@/components/organisms/event/contestEntryWorkFormTypes";

export const CONTEST_ENTRY_APPLICANT_FORM_HISTORY_KEY = "contestEntryApplicantForm";
export const CONTEST_ENTRY_WORK_FORM_HISTORY_KEY = "contestEntryWorkForm";
export const CONTEST_ENTRY_TERMS_AGREEMENT_HISTORY_KEY = "contestEntryTermsAgreement";

export type ContestEntryApplicantFormHistoryPayload = {
  contestId: string;
  form: ContestEntryApplicantFormValue;
};

export type ContestEntryTermsAgreementPayload = {
  contestId: string;
};

/** router.push 直後は Vue Router が history.state を上書きするため、遷移完了まで一時保持する。 */
let pendingApplicantFormNavigation: ContestEntryApplicantFormHistoryPayload | null = null;
let pendingWorkFormNavigation: ContestEntryWorkFormHistoryPayload | null = null;
/** 規約同意は応募完了まで残し、Step 間の戻りでも参照できるようにする。 */
let pendingTermsAgreementContestId: string | null = null;

export type ContestEntryWorkFormHistoryPayload = {
  contestId: string;
  form: ContestEntryWorkFormValue;
};

function isApplicantFormValue(value: unknown): value is ContestEntryApplicantFormValue {
  if (!value || typeof value !== "object") return false;
  const form = value as Record<string, unknown>;
  return (
    typeof form.name === "string" &&
    typeof form.nameKana === "string" &&
    typeof form.penname === "string" &&
    typeof form.email === "string" &&
    typeof form.phone === "string" &&
    typeof form.gender === "number" &&
    typeof form.birthDate === "string" &&
    (typeof form.historyOfAwards === "string" || form.historyOfAwards === undefined)
  );
}

function withHistoryOfAwards(form: ContestEntryApplicantFormValue): ContestEntryApplicantFormValue {
  return {
    ...form,
    historyOfAwards: typeof form.historyOfAwards === "string" ? form.historyOfAwards : "",
  };
}

function isHistoryPayload(value: unknown): value is ContestEntryApplicantFormHistoryPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  return typeof payload.contestId === "string" && isApplicantFormValue(payload.form);
}

function isWorkFormValue(value: unknown): value is ContestEntryWorkFormValue {
  if (!value || typeof value !== "object") return false;
  const form = value as Record<string, unknown>;
  return (
    typeof form.categoryId === "string" &&
    (form.workSource === "nola" || form.workSource === "local" || form.workSource === "") &&
    typeof form.novelId === "string" &&
    typeof form.novelTitle === "string" &&
    typeof form.novelImage === "string" &&
    typeof form.novelDescription === "string" &&
    typeof form.workTitle === "string" &&
    typeof form.synopsis === "string" &&
    (form.extraAnswers === undefined || isExtraAnswers(form.extraAnswers))
  );
}

function isExtraAnswers(value: unknown): value is Record<string, string> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  return Object.values(value as Record<string, unknown>).every((item) => typeof item === "string");
}

function withExtraAnswers(form: ContestEntryWorkFormValue): ContestEntryWorkFormValue {
  return {
    ...form,
    extraAnswers: isExtraAnswers(form.extraAnswers) ? { ...form.extraAnswers } : {},
  };
}

function isWorkHistoryPayload(value: unknown): value is ContestEntryWorkFormHistoryPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  return typeof payload.contestId === "string" && isWorkFormValue(payload.form);
}

/** 現在の history エントリに応募者情報フォームをマージして保存する。 */
export function saveApplicantFormToHistoryState(contestId: string, form: ContestEntryApplicantFormValue): void {
  const payload: ContestEntryApplicantFormHistoryPayload = { contestId, form: { ...form } };
  window.history.replaceState(
    {
      ...window.history.state,
      [CONTEST_ENTRY_APPLICANT_FORM_HISTORY_KEY]: payload,
    },
    ""
  );
}

/** history.state から応募者情報を取得する。contestId が一致しない場合は null。 */
export function readApplicantFormFromHistoryState(contestId: string): ContestEntryApplicantFormValue | null {
  const { state } = window.history;
  if (!state || typeof state !== "object") return null;

  const payload = (state as Record<string, unknown>)[CONTEST_ENTRY_APPLICANT_FORM_HISTORY_KEY];
  if (!isHistoryPayload(payload) || payload.contestId !== contestId) return null;

  return withHistoryOfAwards(payload.form);
}

/** Step2 へ push する直前に呼ぶ。 */
export function setPendingApplicantFormNavigation(contestId: string, form: ContestEntryApplicantFormValue): void {
  pendingApplicantFormNavigation = { contestId, form: { ...form } };
}

/**
 * history.state または pending から応募者情報を解決し、見つかった場合は history に書き戻す。
 * Step2 の onMounted は router.push 解決後の replaceState より先に走ることがあるため pending を参照する。
 */
export function resolveApplicantFormHistoryState(contestId: string): ContestEntryApplicantFormValue | null {
  const fromHistory = readApplicantFormFromHistoryState(contestId);
  if (fromHistory) return fromHistory;

  const pending = pendingApplicantFormNavigation;
  pendingApplicantFormNavigation = null;
  if (!pending || pending.contestId !== contestId) return null;

  saveApplicantFormToHistoryState(contestId, pending.form);
  return withHistoryOfAwards(pending.form);
}

export function saveWorkFormToHistoryState(contestId: string, form: ContestEntryWorkFormValue): void {
  const payload: ContestEntryWorkFormHistoryPayload = { contestId, form: withExtraAnswers(form) };
  window.history.replaceState(
    {
      ...window.history.state,
      [CONTEST_ENTRY_WORK_FORM_HISTORY_KEY]: payload,
    },
    ""
  );
}

export function readWorkFormFromHistoryState(contestId: string): ContestEntryWorkFormValue | null {
  const { state } = window.history;
  if (!state || typeof state !== "object") return null;

  const payload = (state as Record<string, unknown>)[CONTEST_ENTRY_WORK_FORM_HISTORY_KEY];
  if (!isWorkHistoryPayload(payload) || payload.contestId !== contestId) return null;

  return withExtraAnswers(payload.form);
}

export function setPendingWorkFormNavigation(contestId: string, form: ContestEntryWorkFormValue): void {
  pendingWorkFormNavigation = { contestId, form: { ...form } };
}

function isTermsAgreementPayload(value: unknown): value is ContestEntryTermsAgreementPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  return typeof payload.contestId === "string" && payload.contestId.length > 0;
}

/** 現在の history エントリに規約同意をマージして保存する。 */
export function saveTermsAgreementToHistoryState(contestId: string): void {
  if (!contestId) return;
  const payload: ContestEntryTermsAgreementPayload = { contestId };
  window.history.replaceState(
    {
      ...window.history.state,
      [CONTEST_ENTRY_TERMS_AGREEMENT_HISTORY_KEY]: payload,
    },
    ""
  );
}

/** history.state の規約同意が contestId と一致するか。 */
export function readTermsAgreementFromHistoryState(contestId: string): boolean {
  const { state } = window.history;
  if (!state || typeof state !== "object") return false;

  const payload = (state as Record<string, unknown>)[CONTEST_ENTRY_TERMS_AGREEMENT_HISTORY_KEY];
  return isTermsAgreementPayload(payload) && payload.contestId === contestId;
}

/** 規約ページから Step1 へ push する直前に呼ぶ。 */
export function setPendingTermsAgreement(contestId: string): void {
  if (!contestId) return;
  pendingTermsAgreementContestId = contestId;
}

/**
 * history.state または pending から規約同意を解決し、見つかった場合は history に書き戻す。
 * Step1 の初期化は router.push 解決後の replaceState より先に走ることがあるため pending を参照する。
 * pending は消さない。戻る操作で新しい history エントリになっても、同じタブの応募中は同意済みのままにする。
 */
export function resolveTermsAgreement(contestId: string): boolean {
  if (readTermsAgreementFromHistoryState(contestId)) return true;
  if (pendingTermsAgreementContestId !== contestId) return false;

  saveTermsAgreementToHistoryState(contestId);
  return true;
}

function carryTermsAgreement(contestId: string): void {
  if (pendingTermsAgreementContestId === contestId || readTermsAgreementFromHistoryState(contestId)) {
    setPendingTermsAgreement(contestId);
    saveTermsAgreementToHistoryState(contestId);
  }
}

/** 現在の history エントリと pending から応募フォームと規約同意を削除する。 */
export function clearContestEntryFormHistoryState(): void {
  pendingApplicantFormNavigation = null;
  pendingWorkFormNavigation = null;
  pendingTermsAgreementContestId = null;

  const { state } = window.history;
  if (!state || typeof state !== "object") return;

  const nextState = { ...(state as Record<string, unknown>) };
  delete nextState[CONTEST_ENTRY_APPLICANT_FORM_HISTORY_KEY];
  delete nextState[CONTEST_ENTRY_WORK_FORM_HISTORY_KEY];
  delete nextState[CONTEST_ENTRY_TERMS_AGREEMENT_HISTORY_KEY];
  window.history.replaceState(nextState, "");
}

export function resolveWorkFormHistoryState(contestId: string): ContestEntryWorkFormValue | null {
  const fromHistory = readWorkFormFromHistoryState(contestId);
  if (fromHistory) return fromHistory;

  const pending = pendingWorkFormNavigation;
  pendingWorkFormNavigation = null;
  if (!pending || pending.contestId !== contestId) return null;

  saveWorkFormToHistoryState(contestId, pending.form);
  return withExtraAnswers(pending.form);
}

type ContestEntryFormsToPersist = {
  applicantForm?: ContestEntryApplicantFormValue;
  workForm?: ContestEntryWorkFormValue;
};

function isNavigationDuplicated(error: unknown): boolean {
  return !!error && typeof error === "object" && "name" in error && error.name === "NavigationDuplicated";
}

function persistContestEntryForms(contestId: string, forms: ContestEntryFormsToPersist): void {
  if (forms.applicantForm) {
    saveApplicantFormToHistoryState(contestId, forms.applicantForm);
  }
  if (forms.workForm) {
    saveWorkFormToHistoryState(contestId, forms.workForm);
  }
  carryTermsAgreement(contestId);
}

/**
 * history へ保存してから push し、遷移完了後（NavigationDuplicated を含む）にもう一度保存する。
 * router.push が history.state を上書きするため、pending も同時に積む。
 */
export async function pushWithContestEntryForms(
  push: (location: Location) => Promise<unknown>,
  location: Location,
  contestId: string,
  forms: ContestEntryFormsToPersist
): Promise<void> {
  if (forms.applicantForm) {
    setPendingApplicantFormNavigation(contestId, forms.applicantForm);
  }
  if (forms.workForm) {
    setPendingWorkFormNavigation(contestId, forms.workForm);
  }
  persistContestEntryForms(contestId, forms);

  try {
    await push(location);
    persistContestEntryForms(contestId, forms);
  } catch (error) {
    if (isNavigationDuplicated(error)) {
      persistContestEntryForms(contestId, forms);
      return;
    }
    throw error;
  }
}

/**
 * 規約同意を history へ保存してから push し、遷移完了後にもう一度保存する。
 * router.push が history.state を上書きするため、pending も同時に積む。
 */
export async function pushWithTermsAgreement(
  push: (location: Location) => Promise<unknown>,
  location: Location,
  contestId: string
): Promise<void> {
  setPendingTermsAgreement(contestId);
  saveTermsAgreementToHistoryState(contestId);

  try {
    await push(location);
    saveTermsAgreementToHistoryState(contestId);
  } catch (error) {
    if (isNavigationDuplicated(error)) {
      saveTermsAgreementToHistoryState(contestId);
      return;
    }
    throw error;
  }
}
